/* oxlint-disable typescript/no-explicit-any -- schema library requires flexible any types */
/* oxlint-disable typescript/no-unsafe-assignment, typescript/no-unsafe-call, typescript/no-unsafe-member-access -- schema library uses any types throughout */
import { BaseSchema } from '../core/base-schema'
import type * as errors from '../core/errors'
import * as types from '../core/types'
import type * as util from '../core/util'
import type { DefaultSchema } from './wrappers'
import { _default as makeDefault } from './wrappers'
import type { OptionalSchema, NullableSchema } from '../core/base-schema'

// String schema definition
export interface StringSchemaDef extends types.SchemaTypeDef {
  type: 'string'
}

export interface StringSchemaInternals extends types.SchemaInternals<
  string,
  string
> {
  def: StringSchemaDef
  bag: util.LoosePartial<{
    minimum: number
    maximum: number
    patterns: Set<RegExp>
  }>
}

export interface StringSchema extends types.Schema<
  string,
  string,
  StringSchemaInternals
> {
  min(
    length: number,
    message?: string | { message: string | (() => string) },
  ): this
  max(
    length: number,
    message?: string | { message: string | (() => string) },
  ): this
  length(
    length: number,
    message?: string | { message: string | (() => string) },
  ): this
  regex(
    pattern: RegExp,
    message?: string | { message: string | (() => string) },
  ): this
  startsWith(
    prefix: string,
    message?: string | { message: string | (() => string) },
  ): this
  endsWith(
    suffix: string,
    message?: string | { message: string | (() => string) },
  ): this
  includes(
    substring: string,
    message?: string | { message: string | (() => string) },
  ): this
  email(message?: string | { message: string | (() => string) }): this
  httpUrl(message?: string | { message: string | (() => string) }): this
  uppercase(message?: string | { message: string | (() => string) }): this
  lowercase(message?: string | { message: string | (() => string) }): this
  trim(): this
  optional(): OptionalSchema<this>
  nullable(): NullableSchema<this>
  default(defaultValue: string | (() => string)): DefaultSchema<this>
  meta(metadata: Record<string, any>): this
  parse(data: unknown, params?: types.ParseContext): string
  safeParse(
    data: unknown,
    params?: types.ParseContext,
  ):
    | { success: true; data: string }
    | { success: false; error: errors.SchemaError }
}

export const StringSchema = types.$constructor<StringSchema>(
  'StringSchema',
  (inst, def: StringSchemaDef) => {
    BaseSchema.init(inst, def)

    inst._zod.parse = (payload, _ctx) => {
      if (typeof payload.value === 'string') return payload

      payload.issues.push({
        code: 'invalid_type',
        expected: 'string',
        input: payload.value,
        inst,
      })
      return payload
    }

    // Helper to create check
    const createCheck = (
      checkFn: types.CheckFn<string>,
      onattach: (schema: any) => void = () => {},
    ) => ({
      _zod: {
        def: { check: 'custom' },
        check: checkFn,
        onattach: [onattach],
      },
    })

    // Add a check to the schema
    const addCheck = (check: types.Check<string>) => {
      return new StringSchema({
        ...def,
        checks: [...(def.checks ?? []), check],
      })
    }

    inst.min = (length, message) => {
      const getMessage = () =>
        typeof message === 'string' ? message : message?.message
      return addCheck(
        createCheck(
          (payload) => {
            if (codePointLength(payload.value) >= length) return
            const msg = getMessage()
            payload.issues.push({
              code: 'too_small',
            continue: true,
              type: 'string',
              minimum: length,
              inclusive: true,
              input: payload.value,
              inst,
              ...(msg
                ? { message: typeof msg === 'function' ? msg() : msg }
                : {}),
            })
          },
          (schema) => {
            const curr = schema._zod.bag.minimum ?? 0
            if (length > curr) schema._zod.bag.minimum = length
          },
        ),
      )
    }

    inst.max = (length, message) => {
      const getMessage = () =>
        typeof message === 'string' ? message : message?.message
      return addCheck(
        createCheck(
          (payload) => {
            if (codePointLength(payload.value) <= length) return
            const msg = getMessage()
            payload.issues.push({
              code: 'too_big',
            continue: true,
              type: 'string',
              maximum: length,
              inclusive: true,
              input: payload.value,
              inst,
              ...(msg
                ? { message: typeof msg === 'function' ? msg() : msg }
                : {}),
            })
          },
          (schema) => {
            const curr = schema._zod.bag.maximum ?? Number.POSITIVE_INFINITY
            if (length < curr) schema._zod.bag.maximum = length
          },
        ),
      )
    }

    inst.length = (length, message) => {
      const getMessage = () =>
        typeof message === 'string' ? message : message?.message
      return addCheck(
        createCheck((payload) => {
          if (codePointLength(payload.value) === length) return
          const msg = getMessage()
          payload.issues.push({
            code: codePointLength(payload.value) < length ? 'too_small' : 'too_big',
            type: 'string',
            ...(codePointLength(payload.value) < length ? { minimum: length } : { maximum: length }),
            exact: true,
            inclusive: true,
            continue: true,
            input: payload.value,
            inst,
            ...(msg ? { message: typeof msg === 'function' ? msg() : msg } : {}),
          })
        }),
      )
    }

    inst.regex = (pattern, message) => {
      const getMessage = () =>
        typeof message === 'string' ? message : message?.message
      return addCheck(
        createCheck((payload) => {
          pattern.lastIndex = 0
          if (pattern.test(payload.value)) return
          const msg = getMessage()
          payload.issues.push({
            code: 'invalid_string',
            continue: true,
            validation: 'regex',
            pattern: pattern.toString(),
            input: payload.value,
            inst,
            ...(msg
              ? { message: typeof msg === 'function' ? msg() : msg }
              : {}),
          })
        }),
      )
    }

    inst.startsWith = (prefix, message) => {
      const getMessage = () =>
        typeof message === 'string' ? message : message?.message
      return addCheck(
        createCheck((payload) => {
          if (payload.value.startsWith(prefix)) return
          const msg = getMessage()
          payload.issues.push({
            code: 'invalid_string',
            continue: true,
            validation: 'startsWith',
            value: prefix,
            input: payload.value,
            inst,
            ...(msg
              ? { message: typeof msg === 'function' ? msg() : msg }
              : {}),
          })
        }),
      )
    }

    inst.endsWith = (suffix, message) => {
      const getMessage = () =>
        typeof message === 'string' ? message : message?.message
      return addCheck(
        createCheck((payload) => {
          if (payload.value.endsWith(suffix)) return
          const msg = getMessage()
          payload.issues.push({
            code: 'invalid_string',
            continue: true,
            validation: 'endsWith',
            value: suffix,
            input: payload.value,
            inst,
            ...(msg
              ? { message: typeof msg === 'function' ? msg() : msg }
              : {}),
          })
        }),
      )
    }

    inst.includes = (substring, message) => {
      const getMessage = () =>
        typeof message === 'string' ? message : message?.message
      return addCheck(
        createCheck((payload) => {
          if (payload.value.includes(substring)) return
          const msg = getMessage()
          payload.issues.push({
            code: 'invalid_string',
            continue: true,
            validation: 'includes',
            value: substring,
            input: payload.value,
            inst,
            ...(msg
              ? { message: typeof msg === 'function' ? msg() : msg }
              : {}),
          })
        }),
      )
    }

    inst.email = (message) => {
      const emailRegex = /^(?!\.)(?!.*\.\.)([A-Za-z0-9_'+.-]*)[A-Za-z0-9_+-]@([A-Za-z0-9][A-Za-z0-9-]*\.)+[A-Za-z]{2,}$/u
      const getMessage = () =>
        typeof message === 'string' ? message : message?.message
      return addCheck(
        createCheck((payload) => {
          if (emailRegex.test(payload.value)) return
          const msg = getMessage()
          payload.issues.push({
            code: 'invalid_string',
            continue: true,
            validation: 'email',
            input: payload.value,
            inst,
            ...(msg
              ? { message: typeof msg === 'function' ? msg() : msg }
              : {}),
          })
        }),
      )
    }

    inst.httpUrl = (message) => {
      const getMessage = () =>
        typeof message === 'string' ? message : message?.message
      return addCheck(
        createCheck((payload) => {
          try {
            const url = new URL(payload.value)
            if (url.protocol === 'http:' || url.protocol === 'https:') return
          } catch {
            // Fall through to error
          }
          const msg = getMessage()
          payload.issues.push({
            code: 'invalid_string',
            continue: true,
            validation: 'url',
            input: payload.value,
            inst,
            ...(msg
              ? { message: typeof msg === 'function' ? msg() : msg }
              : {}),
          })
        }),
      )
    }

    inst.uppercase = (message) => {
      const getMessage = () =>
        typeof message === 'string' ? message : message?.message
      return addCheck(
        createCheck((payload) => {
          if (/^[^a-z]*$/u.test(payload.value)) return
          const msg = getMessage()
          payload.issues.push({ code: 'invalid_format', continue: true, format: 'uppercase', input: payload.value, inst, ...(msg ? { message: typeof msg === 'function' ? msg() : msg } : {}) })
        }),
      )
    }

    inst.lowercase = (message) => {
      const getMessage = () =>
        typeof message === 'string' ? message : message?.message
      return addCheck(
        createCheck((payload) => {
          if (/^[^A-Z]*$/u.test(payload.value)) return
          const msg = getMessage()
          payload.issues.push({ code: 'invalid_format', continue: true, format: 'lowercase', input: payload.value, inst, ...(msg ? { message: typeof msg === 'function' ? msg() : msg } : {}) })
        }),
      )
    }

    inst.trim = () => {
      return addCheck(
        createCheck((payload) => {
          payload.value = payload.value.trim()
        }),
      )
    }

    inst.default = (defaultValue: string | (() => string)) => {
      return makeDefault(inst, defaultValue)
    }
  },
)

export function string(
  message?: string | { message: string | (() => string) },
): StringSchema {
  return new StringSchema({
    type: 'string',
    ...(message
      ? { error: typeof message === 'string' ? message : message.message }
      : {}),
  })
}

// Zod 4.5.4 util.codePointLength: count code points, not grapheme clusters.
function codePointLength(value: string): number {
  let length = value.length
  for (let i = 0; i < value.length; i++) {
    const point = value.codePointAt(i) ?? 0
    if (point > 0xffff) { length--; i++ }
  }
  return length
}
